/**
 * Note:
 * This file and other related to Modal Dialog, created by MFoster in 2014, later modified during 2014-2017 in scope of glu/core.
 * In 2017 Alex U. moved to dedicated module, and in 2018 alundiak modified related files and published as NPM module.
 */

import { $, util } from '@glu/core';
import locale from '@glu/locale';
import GluIcons from '@glu/icons';
import { createTheme, legacyTheme } from '@glu/theming/glu-css-override';
import ModalView from './views/modalView';
import NotificationView from './views/notificationView';

import './dialog.styles';

let baseTheme = legacyTheme;
let theme = {};
let fullTheme = createTheme(baseTheme, theme);

const updateTheme = () => {
  fullTheme = createTheme(baseTheme, theme);
};

const gluIconsInstance = new GluIcons({});
gluIconsInstance.registerIconHelper();

function bindDialogButtons(view) {
  if (!view.dialogButtons) {
    return [];
  }

  return util.map(util.result(view, 'dialogButtons'), (button) => {
    const ret = util.extend({}, button);
    if (button.callback && util.isFunction(button.callback)) {
      ret.callback = util.bind(button.callback, view);
    } else if (button.callback && util.isString(button.callback) && util.isFunction(view[button.callback])) {
      ret.callback = util.bind(view[button.callback], view);
    }
    return ret;
  });
}

export default {
  // ModalView: ModalView, // Deprecated.
  notifications: [],
  modals: [],
  notificationTopOffset: 15,
  notificationSpacing: 10,
  activeModal: false,

  // null - doesn't handle it, so they clobber each other,
  // 'replace' - will close the current one.
  // 'stack' - waits for the active one to close to show the new one
  // 'queue' - ?
  collisionMode: null,

  setBaseTheme(newBase) {
    baseTheme = newBase;

    updateTheme();
  },

  setTheme(newTheme) {
    theme = newTheme;

    updateTheme();
  },

  getThemingOptions(options) {
    const themeResult = options.theme ? (
      createTheme(baseTheme, { ...theme, ...options.theme })
    ) : fullTheme;

    const modalOptions = {
      ...themeResult,
      type: options.type
    };

    const { legacyModal: { icons = {} } = {} } = themeResult.theme;
    const icon = icons[options.type];

    return icon ? {
      ...modalOptions,
      icon
    } : modalOptions;
  },

  showPopup(view) {
    if (util.isFunction(view.determineFocus)) {
      this.viewPopup(view);
    } else {
      this.bootstrapPopup(view);
    }
  },

  /**
   * Uses an instance of a ModalView to enable modal functionality.
   */
  viewPopup(view) {
    if (this.activeModal && this.collisionMode === 'queue') {
      this.modals.push(view);
    } else if (this.activeModal && this.collisionMode === 'replace') {
      this.activeModal.close();
      this.renderModal(view);
    } else if (this.activeModal && this.collisionMode === 'stack') {
      this.modals.push(this.activeModal);
      this.renderModal(view);
    } else {
      this.renderModal(view);
    }
  },

  renderModal(view) {
    view.collisionMode = this.collisionMode;
    view.render();
    view.on('close', util.bind(this.handleCleanup, this, view));
    this.activeModal = view;
  },

  /**
   * Use bootstrap's jQuery plugin to push a view's element through
   * the plugin to enable modal functionality.
   */
  bootstrapPopup(view) {
    const { $el } = view.render();
    const self = this;

    $el.modal('show')
      .on('hidden', view.close);

    view.on('close', () => {
      $el.modal('hide').remove();
    });

    view.on('close', util.bind(this.handleCleanup, this, view));

    view.on('show', () => {
      self.determineFocus($el);
    });

    // ensure we always close the view if the modal is hidden to avoid orphaned views
    view.$el.on('hide.bs.modal', () => {
      view.close();
    });

    $('body').append($el);

    $el.on('keydown keypress', util.bind(this.handleKeydown, this, $el));

    // enables onShow in views for functionality that needs to happen after DOM insertion
    view.triggerMethod('show');

    // Keep a reference to the modal so that it can be closed via `dialog.close()`
    this.activeModal = view;
  },

  handleCleanup(view) {
    if (view === this.activeModal) {
      this.activeModal = false;
    }

    if (this.collisionMode === 'queue' && this.modals.length > 0) {
      this.showPopup(this.modals.shift());
    } else if (this.collisionMode === 'stack' && this.modals.length > 0) {
      this.activeModal = this.modals.pop();
    }
  },

  determineFocus($el) {
    const $tabbables = this.getTabbables($el);
    if ($tabbables.length > 0) {
      $tabbables.eq(0).focus();
    } else {
      $el.find('.glu-dialog').focus();
    }
  },

  getTabbables($el, ...args) {
    return ModalView.prototype.getTabbables.apply(this, [$el, [...args]]);
  },

  handleKeydown($el, e, ...args) {
    ModalView.prototype.handleKeyPress.apply(this, [$el, e, ...args]);
  },

  alert(message, title, callback, options = {}) {
    if (typeof title === 'function') {
      callback = title;
      title = null;
    }

    const popup = new ModalView({
      ...this.getThemingOptions({ ...options, type: 'alert' }),
      message,
      title,
      buttons: [{
        text: locale.get('ok'),
        className: 'btn-primary',
        callback() {
          this.close();

          if (callback) {
            callback();
          }
        }
      }]
    });

    popup.$el.attr('role', 'alertdialog');
    this.showPopup(popup);

    return popup;
  },

  confirm(message, title, callback, options = {}) {
    if (typeof title === 'function') {
      callback = title;
      title = null;
    }

    if (typeof title !== 'function' && title === null) {
      title = ' ';
    }

    const popup = new ModalView({
      ...this.getThemingOptions({ ...options, type: 'confirm' }),
      message,
      title,
      buttons: [{
        text: locale.get('yes'),
        className: 'btn-primary',
        callback() {
          this.close();

          if (callback) {
            callback(true);
          }
        }
      }, {
        text: locale.get('no'),
        className: 'btn-tertiary',
        callback() {
          this.close();

          if (callback) {
            callback(false);
          }
        }
      }]
    });

    popup.$el.attr('role', 'alertdialog');
    this.showPopup(popup);

    return popup;
  },

  error(message, title, callback, options = {}) {
    if (typeof title === 'function') {
      callback = title;
      title = null;
    }

    const popup = new ModalView({
      icon: 'warning',
      ...this.getThemingOptions({ ...options, type: 'error' }),
      message,
      title,
      buttons: [{
        text: locale.get('ok'),
        className: 'btn-primary',
        callback() {
          this.close();

          if (callback) {
            callback();
          }
        }
      }]
    });

    popup.$el.attr('role', 'alertdialog');
    this.showPopup(popup);

    return popup;
  },

  warning(message, title, callback, options = {}) {
    if (typeof title === 'function') {
      callback = title;
      title = null;
    }

    const popup = new ModalView({
      icon: 'exclamation-solid',
      ...this.getThemingOptions({ ...options, type: 'warning' }),
      message,
      title,
      buttons: [{
        text: locale.get('ok'),
        className: 'btn-primary',
        callback() {
          this.close();

          if (callback) {
            callback();
          }
        }
      }]
    });

    popup.$el.attr('role', 'alertdialog');
    this.showPopup(popup);

    return popup;
  },

  info(message, title, callback, options = {}) {
    if (typeof title === 'function') {
      callback = title;
      title = null;
    }

    const popup = new ModalView({
      icon: 'exclamation-solid',
      ...this.getThemingOptions({ ...options, type: 'info' }),
      message,
      title,
      buttons: [{
        text: locale.get('ok'),
        className: 'btn-primary',
        callback() {
          this.close();

          if (callback) {
            callback();
          }
        }
      }]
    });

    popup.$el.attr('role', 'alertdialog');
    this.showPopup(popup);

    return popup;
  },

  success(message, title, callback, options = {}) {
    if (typeof title === 'function') {
      callback = title;
      title = null;
    }

    const popup = new ModalView({
      icon: 'tick',
      ...this.getThemingOptions({ ...options, type: 'success' }),
      message,
      title,
      buttons: [{
        text: locale.get('ok'),
        className: 'btn-primary',
        callback() {
          this.close();

          if (callback) {
            callback();
          }
        }
      }]
    });

    popup.$el.attr('role', 'alertdialog');
    this.showPopup(popup);

    return popup;
  },

  /**
   * Open a dialog using the specified view and optional overrides.
   *
   * NOTE: the second argument of this method has been repurposed as an options array.  Legacy
   * callers use the second argument as the dialog title.
   */
  open(view, options, buttons, icon, modalClass) {
    // first things first, try to derive dialog options from the view itself.
    const dialogOptions = this.getDialogOptions(view, options);

    // next, use the function arguments to override anything that was not supplied by the view.
    if (util.isObject(options)) {
      util.defaults(dialogOptions, options);
      this.$dialogTrigger = options.trigger || null;
    } else {
      util.defaults(dialogOptions, {
        title: options,
        icon,
        modalClass,
        buttons
      });
    }

    // initialize and open the dialog
    const dialog = new ModalView(dialogOptions);
    dialog.setBody(view);
    this.showPopup(dialog);

    return dialog;
  },

  /**
   * Inspect the view for dialog configuration options.  Newer views should supply
   * functions that return dialog configuration options.  Older views will use a standard set of attributes.
   * We need to support both modes for the time being.
   */
  getDialogOptions(view, options) {
    const dialogTitle = util.isFunction(view.dialogTitle) ? view.dialogTitle() : view.dialogTitle;
    return {
      ...this.getThemingOptions(typeof options === 'object' ? options : {}),
      type: 'custom',
      message: locale.get('loading'),
      title: util.isFunction(view.getHeader) ? view.getHeader() : dialogTitle,
      icon: util.isFunction(view.getHeaderIcon) ? view.getHeaderIcon() : view.dialogTitleIcon,
      modalClass: util.isFunction(view.getClass) ? view.getClass() : view.modalClass,
      buttons: util.isFunction(view.getButtons) ? view.getButtons() : bindDialogButtons(view)
    };
  },

  custom(viewInstance) {
    this.showPopup(viewInstance);
  },

  visible() {
    return $('body').hasClass('modal-open');
  },

  close() {
    if (this.activeModal) {
      this.activeModal.close();
      if (this.$dialogTrigger) {
        this.$dialogTrigger.focus();
        this.$dialogTrigger = null;
      }
    }
  },

  notify(message, type, duration) {
    const notification = new NotificationView({
      message,
      type,
      duration
    });

    this.notifications.push(notification);

    notification.on('close', util.bind(this.removeNotification, this, notification));

    notification.render();

    this.resetNotifications();
  },

  resetNotifications() {
    let topOffset = this.notificationTopOffset;
    const spacing = this.notificationSpacing;
    util.each(this.notifications, (notification) => {
      notification.setTop(topOffset);
      const height = notification.height();
      topOffset += height + spacing;
    });
  },

  removeNotification(notification) {
    const index = this.notifications.indexOf(notification);
    // remove notifications from the list
    this.notifications.splice(index, 1);

    this.resetNotifications();
  }
};
